import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { FileText, Upload, X, Download } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const DOC_TYPES = [
  { key: 'bilan', label: 'Bilans', color: '#E0F2F0', text: '#2E665E' },
  { key: 'compte_rendu', label: 'Comptes rendus', color: '#DBEAFE', text: '#1E3A8A' },
  { key: 'ordonnance', label: 'Ordonnances', color: '#FEF3C7', text: '#92400E' },
  { key: 'certificat', label: 'Certificats médicaux', color: '#FCE7F3', text: '#9D174D' },
  { key: 'administratif', label: 'Pièces administratives', color: '#F3E8FF', text: '#6B21A8' },
  { key: 'autre', label: 'Autres', color: '#F5F5F4', text: '#57534E' },
];

const getTypeInfo = (key) => DOC_TYPES.find(t => t.key === key) || DOC_TYPES[DOC_TYPES.length - 1];

export default function PatientDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', category: 'autre', file: null });
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState('tous');
  const [deletingDoc, setDeletingDoc] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (user) loadData(); }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: docs } = await supabase
        .from('documents')
        .select('*')
        .eq('patient_id', user.id)
        .order('created_at', { ascending: false });
      setDocuments(docs || []);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.file) return;
    setUploading(true);
    try {
      const ext = uploadData.file.name.split('.').pop();
      const fileName = `patient-doc-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, uploadData.file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('documents').getPublicUrl(fileName);
      await supabase.from('documents').insert({
        patient_id: user.id,
        title: uploadData.title,
        type: uploadData.category,
        category: uploadData.category,
        file_url: urlData.publicUrl,
        file_name: uploadData.file.name,
        uploaded_by_id: user.id,
        uploaded_by_type: 'patient',
        created_at: new Date().toISOString(),
      });
      setShowUpload(false);
      setUploadData({ title: '', category: 'autre', file: null });
      loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.title}" ?`)) return;
    setDeletingDoc(doc.id);
    try {
      await supabase.from('documents').delete().eq('id', doc.id);
      loadData();
    } finally {
      setDeletingDoc(null);
    }
  };

  const filteredDocs = filterType === 'tous' ? documents : documents.filter(d => (d.category || d.type) === filterType);

  if (loading) return (
    <div className="p-5 space-y-3">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-stone-100 rounded-3xl animate-pulse" />)}
    </div>
  );

  return (
    <div className="px-5 pt-5 pb-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="page-title">Documents</h1>
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full font-heading font-semibold text-sm"
          style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
          <Upload size={15} /> Ajouter
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
        <button onClick={() => setFilterType('tous')}
          className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-heading font-semibold transition-all ${filterType === 'tous' ? 'bg-sage-500 text-white' : 'bg-white text-slate-500 border border-stone-200'}`}>
          Tous
        </button>
        {DOC_TYPES.map(t => (
          <button key={t.key} onClick={() => setFilterType(t.key)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-heading font-semibold transition-all ${filterType === t.key ? 'bg-sage-500 text-white' : 'bg-white text-slate-500 border border-stone-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {filteredDocs.length === 0 ? (
        <div className="passerelle-card text-center py-10">
          <FileText size={28} className="text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400 font-body">Aucun document</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocs.map(doc => {
            const typeInfo = getTypeInfo(doc.category || doc.type);
            const date = doc.created_at ? format(new Date(doc.created_at), 'd MMM yyyy', { locale: fr }) : '';
            return (
              <div key={doc.id} className="passerelle-card flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: typeInfo.color }}>
                  <FileText size={20} style={{ color: typeInfo.text }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-slate-800 leading-tight">{doc.title}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: typeInfo.color, color: typeInfo.text }}>
                      {typeInfo.label}
                    </span>
                    <span className="text-xs text-slate-400 font-body">{date}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {doc.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noreferrer"
                      className="w-9 h-9 rounded-xl bg-sage-50 flex items-center justify-center text-sage-600">
                      <Download size={16} />
                    </a>
                  )}
                  <button onClick={() => deleteDocument(doc)} disabled={deletingDoc === doc.id}
                    className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-400">
                    <X size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal upload */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-slate-800">Ajouter un document</h3>
              <button onClick={() => setShowUpload(false)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Titre *</label>
                <input type="text" value={uploadData.title} onChange={e => setUploadData(p => ({...p, title: e.target.value}))}
                  placeholder="Ex: Bilan orthophonique 2024"
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Catégorie</label>
                <select value={uploadData.category} onChange={e => setUploadData(p => ({...p, category: e.target.value}))}
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700">
                  {DOC_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Fichier *</label>
                <input ref={fileRef} type="file" className="hidden" onChange={e => setUploadData(p => ({...p, file: e.target.files[0]}))} />
                {uploadData.file ? (
                  <div className="flex items-center gap-2 p-3 bg-sage-50 rounded-xl border border-sage-200">
                    <FileText size={16} className="text-sage-600" />
                    <span className="text-sm font-body text-sage-700 flex-1 truncate">{uploadData.file.name}</span>
                    <button onClick={() => setUploadData(p => ({...p, file: null}))}><X size={14} className="text-slate-400" /></button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()}
                    className="w-full py-4 border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center gap-2 text-slate-400">
                    <Upload size={20} />
                    <span className="text-sm font-heading font-semibold">Sélectionner un fichier</span>
                  </button>
                )}
              </div>
            </div>
            <button onClick={handleUpload} disabled={uploading || !uploadData.title || !uploadData.file}
              className="w-full mt-5 h-12 rounded-2xl font-heading font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
              Déposer le document
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
